import { AuditTrigger } from '@/types/audit';

export const auditTriggers: AuditTrigger[] = [
  {
    id: 1,
    name: "Delayed or Inaccurate CRILC Reporting",
    riskLevel: "high",
    description: "Issues with large exposure reporting to RBI's Central Repository of Information on Large Credits",
    trigger: "Exposure > ₹5 crore not reported within RBI timelines, or inconsistencies between CRILC and internal loan books.",
    risk: "Regulatory censure; signals poor credit risk governance.",
    preventiveAction: "System scans for overdue large accounts daily and cross-checks CRILC formats.",
    category: "regulatory",
    status: "active",
    lastChecked: "2025-04-30",
    complianceScore: 85,
    contextData: {
      impactAreas: ["Regulatory Compliance", "Credit Risk Management", "Reporting Accuracy"],
      historicalTrends: [
        { period: "January 2025", score: 90 },
        { period: "February 2025", score: 88 },
        { period: "March 2025", score: 87 },
        { period: "April 2025", score: 85 }
      ],
      regulatoryReferences: [
        {
          name: "RBI Circular on CRILC Reporting - DBOD.BP.BC.No.97/21.04.132/2013-14",
          description: "Framework for categorizing SMA accounts and reporting to Central Repository of Information on Large Credits",
          link: "#"
        },
        {
          name: "RBI FAQ on CRILC Reporting - 2019 Update",
          description: "Revised guidelines on reporting formats and schedules for borrower exposures",
          link: "#"
        }
      ],
      recommendedActions: [
        {
          action: "Implement daily data reconciliation between core banking system and CRILC reporting module",
          priority: "critical",
          effort: "medium",
          responsibleTeam: "Credit Operations"
        },
        {
          action: "Establish systematic validation checks for large exposures before submission",
          priority: "high",
          effort: "medium",
          responsibleTeam: "Risk Management"
        },
        {
          action: "Create automated alerts for exposures crossing ₹4.5 crore threshold",
          priority: "medium",
          effort: "small",
          responsibleTeam: "IT"
        }
      ],
      relatedDocuments: [
        {
          name: "CRILC Reporting Policy",
          type: "Internal Policy",
          lastUpdated: "2025-01-15"
        },
        {
          name: "Large Exposure Management Framework",
          type: "Procedure Document",
          lastUpdated: "2024-12-08"
        },
        {
          name: "SMA Classification Guidelines",
          type: "Operational Manual",
          lastUpdated: "2025-03-22"
        }
      ]
    }
  },
  {
    id: 2,
    name: "Breach of Capital Adequacy or Leverage Norms",
    riskLevel: "high",
    description: "Capital levels approaching regulatory minimums",
    trigger: "Capital Adequacy Ratio (CAR) dropping close to minimum (15% for most NBFCs).",
    risk: "RBI inspection triggers and business restrictions if CAR dips below threshold.",
    preventiveAction: "Real-time tracking of Tier I/II capital vs. risk-weighted assets, and flagging when CAR < 16%.",
    category: "financial",
    status: "monitoring",
    lastChecked: "2025-05-01",
    complianceScore: 92,
    contextData: {
      impactAreas: ["Capital Management", "Regulatory Compliance", "Business Growth"],
      historicalTrends: [
        { period: "January 2025", score: 88 },
        { period: "February 2025", score: 90 },
        { period: "March 2025", score: 91 },
        { period: "April 2025", score: 92 }
      ],
      regulatoryReferences: [
        {
          name: "RBI Master Direction - Non-Banking Financial Company - Systemically Important",
          description: "Guidelines on minimum capital requirements and capital adequacy ratios for NBFCs",
          link: "#"
        }
      ],
      recommendedActions: [
        {
          action: "Implement daily CAR monitoring dashboard for executive team",
          priority: "high",
          effort: "medium",
          responsibleTeam: "Finance"
        },
        {
          action: "Establish capital raising contingency plan for potential breaches",
          priority: "high",
          effort: "large",
          responsibleTeam: "Treasury"
        },
        {
          action: "Review risk-weighted asset calculations for optimization opportunities",
          priority: "medium",
          effort: "medium",
          responsibleTeam: "Risk Management"
        }
      ],
      relatedDocuments: [
        {
          name: "Capital Management Policy",
          type: "Board Approved Policy",
          lastUpdated: "2025-02-10"
        },
        {
          name: "Capital Planning Framework",
          type: "Strategic Document",
          lastUpdated: "2025-01-30"
        }
      ]
    }
  },
  {
    id: 3,
    name: "Asset-Liability Mismatch (ALM Buckets)",
    riskLevel: "medium",
    description: "Potential liquidity issues in short-term time buckets",
    trigger: "Negative cumulative mismatches in short-term buckets (1-7, 8-14, 15-30 days).",
    risk: "RBI scrutiny for liquidity risk; possible supervisory restrictions.",
    preventiveAction: "Continuous ALM analysis with alerts when any mismatch exceeds pre-defined % thresholds.",
    category: "financial",
    status: "active",
    lastChecked: "2025-04-29",
    complianceScore: 78,
    contextData: {
      impactAreas: ["Liquidity Management", "Funding Strategy", "Regulatory Compliance"],
      historicalTrends: [
        { period: "January 2025", score: 85 },
        { period: "February 2025", score: 82 },
        { period: "March 2025", score: 80 },
        { period: "April 2025", score: 78 }
      ],
      regulatoryReferences: [
        {
          name: "RBI Guidelines on Liquidity Risk Management - 2020",
          description: "Framework for monitoring and managing structural and dynamic liquidity",
          link: "#"
        }
      ],
      recommendedActions: [
        {
          action: "Implement liquidity stress testing on quarterly basis",
          priority: "high",
          effort: "medium",
          responsibleTeam: "Treasury"
        },
        {
          action: "Diversify funding sources to reduce concentration risk",
          priority: "high",
          effort: "large",
          responsibleTeam: "Finance"
        },
        {
          action: "Create automated daily ALM bucket variance reports",
          priority: "medium",
          effort: "small",
          responsibleTeam: "IT"
        }
      ],
      relatedDocuments: [
        {
          name: "Asset Liability Management Policy",
          type: "Board Approved Policy",
          lastUpdated: "2024-12-15"
        },
        {
          name: "Liquidity Contingency Plan",
          type: "Strategic Document",
          lastUpdated: "2025-03-05"
        }
      ]
    }
  },
  {
    id: 4,
    name: "KYC/AML Compliance Gaps",
    riskLevel: "high",
    description: "Customer verification and AML processes not up to standard",
    trigger: "High percentage of disbursements without valid PAN/Aadhaar, delayed CKYCR uploads, or incomplete CERSAI tagging.",
    risk: "Heavy regulatory penalties, especially under PMLA.",
    preventiveAction: "Auto-flag loans missing mandatory fields; dashboard tracking of CKYCR/CERSAI sync compliance.",
    category: "regulatory",
    status: "resolved",
    lastChecked: "2025-04-25",
    complianceScore: 95,
    contextData: {
      impactAreas: ["KYC Compliance", "AML Risk Management", "Customer Onboarding"],
      historicalTrends: [
        { period: "January 2025", score: 82 },
        { period: "February 2025", score: 87 },
        { period: "March 2025", score: 90 },
        { period: "April 2025", score: 95 }
      ],
      regulatoryReferences: [
        {
          name: "RBI KYC Master Direction - 2016 (Updated 2023)",
          description: "Comprehensive guidelines on Know Your Customer norms and Anti-Money Laundering standards",
          link: "#"
        },
        {
          name: "Prevention of Money Laundering Act (PMLA) 2002",
          description: "Legal framework to combat money laundering in India",
          link: "#"
        }
      ],
      recommendedActions: [
        {
          action: "Integrate real-time KYC verification with loan origination system",
          priority: "high",
          effort: "medium",
          responsibleTeam: "Operations"
        },
        {
          action: "Implement automated CKYCR upload process",
          priority: "medium",
          effort: "small",
          responsibleTeam: "IT"
        }
      ],
      relatedDocuments: [
        {
          name: "KYC and AML Policy",
          type: "Board Approved Policy",
          lastUpdated: "2025-02-20"
        },
        {
          name: "Customer Verification Procedure",
          type: "Operational Manual",
          lastUpdated: "2025-04-15"
        }
      ]
    }
  },
  {
    id: 5,
    name: "Inconsistent Credit Bureau Reporting",
    riskLevel: "medium",
    description: "Issues with credit information reporting",
    trigger: "Delayed or missed submission to Credit Bureaus (CICs) or data mismatches across bureaus.",
    risk: "May lead to regulatory penalties and reputational damage.",
    preventiveAction: "Reconciliation engine that highlights data inconsistencies across Experian, CRIF, CIBIL, and Equifax.",
    category: "data",
    status: "monitoring",
    lastChecked: "2025-04-28",
    complianceScore: 88,
    contextData: {
      impactAreas: ["Credit Reporting", "Data Accuracy", "Customer Experience"],
      historicalTrends: [
        { period: "January 2025", score: 84 },
        { period: "February 2025", score: 86 },
        { period: "March 2025", score: 87 },
        { period: "April 2025", score: 88 }
      ],
      regulatoryReferences: [
        {
          name: "Credit Information Companies Regulation Act, 2005",
          description: "Regulation of credit information companies and protection of credit information",
          link: "#"
        }
      ],
      recommendedActions: [
        {
          action: "Implement monthly credit bureau data reconciliation process",
          priority: "medium",
          effort: "medium",
          responsibleTeam: "Data Management"
        },
        {
          action: "Create automated data consistency checks before submission",
          priority: "medium",
          effort: "small",
          responsibleTeam: "IT"
        }
      ],
      relatedDocuments: [
        {
          name: "Credit Bureau Reporting Policy",
          type: "Internal Policy",
          lastUpdated: "2025-01-10"
        }
      ]
    }
  },
  {
    id: 6,
    name: "Irregularities in Outsourcing & FLDG Arrangements",
    riskLevel: "high",
    description: "Non-compliant credit guarantee structures with partners",
    trigger: "First-loss guarantees given to fintech partners in violation of RBI's securitization/co-lending guidelines.",
    risk: "Severe scrutiny under RBI's 2022 FLDG guidelines.",
    preventiveAction: "Contract scan engine that flags non-compliant credit guarantee clauses.",
    category: "partnership",
    status: "active",
    lastChecked: "2025-04-30",
    complianceScore: 72,
    contextData: {
      impactAreas: ["Partner Management", "Contract Compliance", "Credit Risk"],
      historicalTrends: [
        { period: "January 2025", score: 69 },
        { period: "February 2025", score: 70 },
        { period: "March 2025", score: 71 },
        { period: "April 2025", score: 72 }
      ],
      regulatoryReferences: [
        {
          name: "RBI Guidelines on Digital Lending - 2022",
          description: "Framework for LSPs and REs on FLDG arrangements",
          link: "#"
        }
      ],
      recommendedActions: [
        {
          action: "Comprehensive review of all partner contracts for FLDG compliance",
          priority: "critical",
          effort: "large",
          responsibleTeam: "Legal"
        },
        {
          action: "Implement partner compliance assessment framework",
          priority: "high",
          effort: "medium",
          responsibleTeam: "Compliance"
        }
      ],
      relatedDocuments: [
        {
          name: "FLDG Compliance Framework",
          type: "Strategic Document",
          lastUpdated: "2025-03-18"
        },
        {
          name: "Partner Agreement Templates",
          type: "Legal Templates",
          lastUpdated: "2025-04-02"
        }
      ]
    }
  },
  {
    id: 7,
    name: "Lending Outside Board-Approved Policy",
    riskLevel: "medium",
    description: "Loans issued that don't adhere to approved policies",
    trigger: "Loans disbursed above LTV or unsecured limits not authorized in board-approved lending policy.",
    risk: "Governance red flag during inspection.",
    preventiveAction: "Policy-linked underwriting rules that automatically highlight deviations.",
    category: "governance",
    status: "resolved",
    lastChecked: "2025-04-26",
    complianceScore: 90,
    contextData: {
      impactAreas: ["Credit Policy Adherence", "Governance", "Underwriting Quality"],
      historicalTrends: [
        { period: "January 2025", score: 82 },
        { period: "February 2025", score: 85 },
        { period: "March 2025", score: 88 },
        { period: "April 2025", score: 90 }
      ],
      regulatoryReferences: [
        {
          name: "RBI Master Direction for NBFCs",
          description: "Guidelines on governance and policy implementation",
          link: "#"
        }
      ],
      recommendedActions: [
        {
          action: "Implement real-time policy compliance checks in loan origination system",
          priority: "medium",
          effort: "medium",
          responsibleTeam: "Credit"
        },
        {
          action: "Create monthly policy deviation reports for Board review",
          priority: "medium",
          effort: "small",
          responsibleTeam: "Risk Management"
        }
      ],
      relatedDocuments: [
        {
          name: "Credit Policy Manual",
          type: "Board Approved Policy",
          lastUpdated: "2025-03-12"
        },
        {
          name: "Loan Approval Matrix",
          type: "Operational Guidelines",
          lastUpdated: "2025-02-28"
        }
      ]
    }
  },
  {
    id: 8,
    name: "Non-compliance with Fair Practices Code",
    riskLevel: "medium",
    description: "Failure to adhere to customer communication requirements",
    trigger: "Missing communication logs for rejection of loan applications, repossession notices, or interest rate disclosures.",
    risk: "RBI observations under consumer protection guidelines.",
    preventiveAction: "Compliance tracker for mandatory borrower communications and documentation timelines.",
    category: "customer",
    status: "monitoring",
    lastChecked: "2025-04-28",
    complianceScore: 83,
    contextData: {
      impactAreas: ["Customer Communication", "Fair Practices", "Documentation"],
      historicalTrends: [
        { period: "January 2025", score: 80 },
        { period: "February 2025", score: 81 },
        { period: "March 2025", score: 82 },
        { period: "April 2025", score: 83 }
      ],
      regulatoryReferences: [
        {
          name: "RBI Fair Practices Code for NBFCs",
          description: "Guidelines on fair practices to be followed when dealing with customers",
          link: "#"
        }
      ],
      recommendedActions: [
        {
          action: "Implement automated customer communication workflow",
          priority: "medium",
          effort: "medium",
          responsibleTeam: "Customer Operations"
        },
        {
          action: "Create customer communication templates repository",
          priority: "medium",
          effort: "small",
          responsibleTeam: "Legal"
        }
      ],
      relatedDocuments: [
        {
          name: "Fair Practices Code",
          type: "Board Approved Policy",
          lastUpdated: "2025-02-15"
        },
        {
          name: "Customer Communication Guidelines",
          type: "Operational Manual",
          lastUpdated: "2025-03-22"
        }
      ]
    }
  },
  {
    id: 9,
    name: "Delayed Reporting under Statutory Returns",
    riskLevel: "low",
    description: "Late filing of mandatory regulatory reports",
    trigger: "Late filings of NBS-1, NBS-9, NDS returns, or financial results.",
    risk: "Attracts fines and negative supervisory observations.",
    preventiveAction: "Compliance calendar with advance filing reminders, maker-checker dashboard, and filing confirmation capture.",
    category: "regulatory",
    status: "resolved",
    lastChecked: "2025-04-27",
    complianceScore: 96,
    contextData: {
      impactAreas: ["Regulatory Reporting", "Timeline Compliance", "Documentation"],
      historicalTrends: [
        { period: "January 2025", score: 90 },
        { period: "February 2025", score: 92 },
        { period: "March 2025", score: 94 },
        { period: "April 2025", score: 96 }
      ],
      regulatoryReferences: [
        {
          name: "RBI NBFC Returns Guidelines",
          description: "Schedule and format for statutory returns by NBFCs",
          link: "#"
        }
      ],
      recommendedActions: [
        {
          action: "Implement automated regulatory filing calendar with alerts",
          priority: "low",
          effort: "small",
          responsibleTeam: "Compliance"
        }
      ],
      relatedDocuments: [
        {
          name: "Regulatory Reporting Manual",
          type: "Procedure Document",
          lastUpdated: "2025-01-30"
        }
      ]
    }
  },
  {
    id: 10,
    name: "High NPA Divergence (Internal vs. Statutory)",
    riskLevel: "high",
    description: "Discrepancy in NPA classification",
    trigger: "Discrepancy between internal NPA classification and statutory auditor/RBI assessment.",
    risk: "Market credibility hit, mandatory disclosure under RBI directions.",
    preventiveAction: "Auto-classification of NPAs as per IRACP norms; flags mismatches.",
    category: "financial",
    status: "active",
    lastChecked: "2025-04-30",
    complianceScore: 76,
    contextData: {
      impactAreas: ["Asset Classification", "Financial Reporting", "Audit Compliance"],
      historicalTrends: [
        { period: "January 2025", score: 72 },
        { period: "February 2025", score: 73 },
        { period: "March 2025", score: 75 },
        { period: "April 2025", score: 76 }
      ],
      regulatoryReferences: [
        {
          name: "RBI Master Circular on Income Recognition, Asset Classification and Provisioning",
          description: "Guidelines for classifying NPAs and provisioning requirements",
          link: "#"
        },
        {
          name: "RBI Directive on Disclosure of Divergence",
          description: "Requirements for disclosing divergence between bank and RBI's assessment of NPAs",
          link: "#"
        }
      ],
      recommendedActions: [
        {
          action: "Implement system-based asset classification as per IRACP norms",
          priority: "high",
          effort: "large",
          responsibleTeam: "IT & Finance"
        },
        {
          action: "Conduct quarterly NPA classification review by external auditors",
          priority: "high",
          effort: "medium",
          responsibleTeam: "Finance"
        },
        {
          action: "Create automated NPA classification reconciliation reports",
          priority: "medium",
          effort: "medium",
          responsibleTeam: "Risk Management"
        }
      ],
      relatedDocuments: [
        {
          name: "NPA Classification Policy",
          type: "Board Approved Policy",
          lastUpdated: "2025-02-08"
        },
        {
          name: "Provisioning Guidelines",
          type: "Financial Manual",
          lastUpdated: "2025-03-15"
        }
      ]
    }
  }
];
